import {
  Button,
  ButtonsGroup,
  Checkbox,
  Flex,
  Modal,
  ModalProps,
  Space,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import eInvoiceApprovalConfirmationIllustration from '../../../../assets/images/e-invoicing/e-invoice-approval-confirmation.svg'
import * as Styled from './styles'

interface EInvoiceApprovalConfirmationProps extends ModalProps {
  isProcessing?: boolean
  onConfirm: ((options: { shouldNeverShowAgain: boolean }) => void) | null
}

export const EInvoiceApprovalConfirmation = ({
  isProcessing = false,
  onConfirm,
  ...modalProps
}: EInvoiceApprovalConfirmationProps): ReactElement => {
  const { t } = useTranslation()
  const [shouldNeverShowAgain, setShouldNeverShowAgain] = useState(false)
  const { close } = useModal(modalProps.id)

  const handleCheckboxChange = useCallback(() => {
    setShouldNeverShowAgain((prevState) => !prevState)
  }, [])

  const handleCancelButtonClick = useCallback(() => {
    close()
  }, [close])

  const handleApproveButtonClick = useCallback(() => {
    onConfirm?.({ shouldNeverShowAgain })
  }, [onConfirm, shouldNeverShowAgain])

  return (
    <Modal {...modalProps} size="s" closable={!isProcessing}>
      <Modal.Header title={t('receipts.e_invoice.confirm_approval_modal.title')} />
      <Modal.Body>
        <Flex alignItems="center" flexDirection="column" width="100%">
          <Space size="s6" />
          <img alt="approve e-invoice" height={200} src={eInvoiceApprovalConfirmationIllustration} width={200} />
          <Space />
          <Styled.DescriptionWrapper>
            <Styled.DescriptionContent>
              <Text alignment="left" colorVariant="primary">
                {t('receipts.e_invoice.confirm_approval_modal.warning')}
              </Text>
              <Space size="s4" />
              <Text alignment="left" colorVariant="primary">
                {t('receipts.e_invoice.confirm_approval_modal.description')}
              </Text>
            </Styled.DescriptionContent>
          </Styled.DescriptionWrapper>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Styled.FooterContentWrapper>
          <Flex alignItems="center" flexGrow={1} justifyContent="space-between">
            <Checkbox checked={shouldNeverShowAgain} disabled={isProcessing} onChange={handleCheckboxChange}>
              <Text>{t('receipts.e_invoice.confirm_approval_modal.dont_show_again')}</Text>
            </Checkbox>
            <ButtonsGroup>
              <Button disabled={isProcessing} variant="text" onClick={handleCancelButtonClick}>
                {t('receipts.e_invoice.confirm_approval_modal.return')}
              </Button>
              <Button loading={isProcessing} onClick={handleApproveButtonClick}>
                {t('receipts.e_invoice.confirm_approval_modal.approve')}
              </Button>
            </ButtonsGroup>
          </Flex>
        </Styled.FooterContentWrapper>
      </Modal.Footer>
    </Modal>
  )
}

export const EInvoiceApprovalConfirmationModal = withModalConditionalRender(EInvoiceApprovalConfirmation)
